import {
  ArrowRightIcon,
  ArrowUpDownIcon,
  BadgePercentIcon,
  BanknoteIcon,
  BoxIcon,
  BuildingIcon,
  CalculatorIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleIcon,
  CircleMinusIcon,
  CirclePlayIcon,
  CirclePlusIcon,
  CircleXIcon,
  ClipboardIcon,
  ClockIcon,
  Columns3Icon,
  CopyIcon,
  CornerDownRightIcon,
  CreditCardIcon,
  EllipsisIcon,
  ExpandIcon,
  ExternalLinkIcon,
  EyeIcon,
  FileTextIcon,
  FolderDownIcon,
  FolderUpIcon,
  FunnelIcon,
  Grid2x2PlusIcon,
  GripVerticalIcon,
  InboxIcon,
  InfoIcon,
  KeyIcon,
  LinkIcon,
  LockIcon,
  MapPinIcon,
  MenuIcon,
  MessageCircleMoreIcon,
  MoveDownIcon,
  MoveUpIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  RocketIcon,
  SearchIcon,
  SettingsIcon,
  ShoppingCartIcon,
  ShrinkIcon,
  SlidersHorizontalIcon,
  SparklesIcon,
  SquarePenIcon,
  StarIcon,
  StoreIcon,
  TagIcon,
  TicketPercentIcon,
  Trash2Icon,
  TrendingDownIcon,
  TrendingUpIcon,
  TriangleAlertIcon,
  TruckIcon,
  UploadIcon,
  UserIcon,
  UserMinusIcon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import type { FunctionComponent, PropsWithChildren, SVGProps } from 'react';
import { cn } from '../../../lib/styles';
import type { IconName, IconProps, IconSize } from './types';

function MarginedIcon({ children }: PropsWithChildren) {
  return <span className="*:mx-0.5">{children}</span>;
}

const ICON_MAP: Record<IconName, FunctionComponent<SVGProps<SVGSVGElement>>> = {
  add: CirclePlusIcon,
  adjust: SlidersHorizontalIcon,
  alertTriangle: TriangleAlertIcon,
  arrowDown: MoveDownIcon,
  arrowRight: ArrowRightIcon,
  arrowUp: MoveUpIcon,
  back: ChevronLeftIcon,
  bulkUpload: FolderUpIcon,
  calculator: CalculatorIcon,
  calendar: CalendarIcon,
  cart: ShoppingCartIcon,
  chat: MessageCircleMoreIcon,
  check: CheckIcon,
  checkCircle: CircleCheckIcon,
  chevronDown: ChevronDownIcon,
  chevronRight: ChevronRightIcon,
  chevronsLeft: ChevronsLeftIcon,
  chevronsRight: ChevronsRightIcon,
  circle: (props) => <CircleIcon fill="currentColor" {...props} />,
  clock: ClockIcon,
  copy: ClipboardIcon,
  couponRequirement: TicketPercentIcon,
  cube: BoxIcon,
  delete: Trash2Icon,
  document: FileTextIcon,
  download: FolderDownIcon,
  dragHandle: GripVerticalIcon,
  duplicate: CopyIcon,
  edit: SquarePenIcon,
  ellipsis: EllipsisIcon,
  exclamationCircle: CircleAlertIcon,
  externalLink: ExternalLinkIcon,
  filter: FunnelIcon,
  inbox: InboxIcon,
  infoCircle: InfoIcon,
  inherit: CornerDownRightIcon,
  key: KeyIcon,
  link: LinkIcon,
  lock: LockIcon,
  mapPin: MapPinIcon,
  menu: MenuIcon,
  minusCircle: CircleMinusIcon,
  money: BanknoteIcon,
  panelCollapse: ShrinkIcon,
  panelExpand: ExpandIcon,
  percentage: BadgePercentIcon,
  payment: CreditCardIcon,
  playCircle: CirclePlayIcon,
  plusCircle: CirclePlusIcon,
  premium: StarIcon,
  priceList: ReceiptTextIcon,
  refresh: RefreshCwIcon,
  rocket: RocketIcon,
  search: SearchIcon,
  settings: SettingsIcon,
  shipping: MapPinIcon,
  sort: ArrowUpDownIcon,
  store: StoreIcon,
  tag: TagIcon,
  upgrade: SparklesIcon,
  upload: UploadIcon,
  user: UserIcon,
  userAdd: UserPlusIcon,
  userRemove: UserMinusIcon,
  userGroup: UsersIcon,
  change: ArrowUpDownIcon,
  squaresPlus: Grid2x2PlusIcon,
  buildingOffice: BuildingIcon,
  truck: TruckIcon,
  view: EyeIcon,
  viewColumns: Columns3Icon,
  x: XIcon,
  xCircle: CircleXIcon,
  increase: (props) => (
    <MarginedIcon>
      <TrendingUpIcon {...props} />
    </MarginedIcon>
  ),
  decrease: (props) => (
    <MarginedIcon>
      <TrendingDownIcon {...props} />
    </MarginedIcon>
  ),
} as const;

const SIZE_MAP: Record<IconSize, string> = {
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-4.5',
  lg: 'size-5',
  xl: 'size-6',
};

export function Icon({ name, ariaLabel, size = 'sm' }: IconProps) {
  if (!name) {
    return null;
  }

  const IconSource = ICON_MAP[name];
  if (!IconSource) {
    console.error(`[Icon] Missing icon mapping for "${name}"`);
    return null;
  }

  return (
    <span className="shrink-0" aria-label={ariaLabel} role="img" aria-hidden={!ariaLabel}>
      <IconSource aria-hidden className={cn(SIZE_MAP[size], 'stroke-2')} />
    </span>
  );
}
